import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userService, type UserProfile, type UpdateProfileData, type UserStatistics } from '../services/userService';
import { refetchAllCalendarEventQueries } from '../utils/refetchCalendarEvents';

// Ключи для кеша
export const profileKeys = {
  all: ['profile'] as const,
  profile: () => [...profileKeys.all, 'data'] as const,
  statistics: () => [...profileKeys.all, 'statistics'] as const,
  workplaceStats: (userId: number) => [...profileKeys.all, 'statistics', 'workplace', userId] as const,
};

/**
 * Хук для получения профиля пользователя
 */
export const useProfile = () => {
  const queryClient = useQueryClient();
  
  // Получаем данные из кеша синхронно
  const cachedProfile = queryClient.getQueryData<UserProfile>(profileKeys.profile());
  
  const query = useQuery({
    queryKey: profileKeys.profile(),
    queryFn: () => userService.getProfile(),
    staleTime: 1000 * 60 * 5, // 5 минут - профиль меняется реже
    gcTime: 1000 * 60 * 30, // 30 минут - время хранения в кеше
    refetchInterval: 1000 * 60 * 2, // Автоматическое обновление каждые 2 минуты
    refetchIntervalInBackground: false, // Профиль не нужно обновлять в фоне
    // Используем кешированные данные как placeholder для мгновенного отображения
    placeholderData: cachedProfile,
    // Начальные данные для мгновенного отображения
    initialData: cachedProfile,
    initialDataUpdatedAt: cachedProfile ? Date.now() - 1000 : undefined,
    // Не обновлять при монтировании, если есть свежие данные в кеше (они уже загружены в AuthContext)
    refetchOnMount: !cachedProfile,
  });
  
  // Всегда возвращаем данные (из кеша или реальные)
  const data = query.data ?? cachedProfile;
  
  return {
    ...query,
    data,
    // isLoading только если нет данных вообще и идет загрузка
    isLoading: query.isLoading && !cachedProfile && !query.data,
  };
};

/**
 * Хук для получения статистики пользователя
 */
export const useStatistics = () => {
  return useQuery({
    queryKey: profileKeys.statistics(),
    queryFn: () => userService.getStatistics(),
    staleTime: 1000 * 30, // 30 секунд - статистика может меняться часто
    refetchInterval: 1000 * 30, // Автоматическое обновление каждые 30 секунд
    refetchIntervalInBackground: true, // Обновлять даже когда вкладка неактивна
  });
};

/**
 * Хук для обновления профиля
 */
export const useUpdateProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateProfileData) => userService.updateProfile(data),
    onSuccess: (updatedProfile) => {
      // Обновляем кеш профиля
      queryClient.setQueryData(profileKeys.profile(), updatedProfile);
      // Инвалидируем статистику, если нужно
      queryClient.invalidateQueries({ queryKey: [...profileKeys.all, 'statistics'], exact: false });
      // Дни рождения в календаре считаются с сервера по birth_date — сбрасываем кеш списка событий
      void refetchAllCalendarEventQueries(queryClient);

      // Возвращаем обновленный профиль для использования в компоненте
      return updatedProfile;
    },
  });
};
